import { useContext, useEffect, useState, type JSX } from 'react';
import { GiftcardPaymentInfo, PaymentInfo } from '@repo/types';
import { useLocale } from '@repo/i18n';
import { MountPointContext } from '@repo/common-utils/mountPoint';
import { isGiftcardPaymentInfo } from '@repo/widget-utils/payment-types-helper';

declare const window: any;

let netsContainerCount = 0;

interface IProps {
    context: PaymentInfo | GiftcardPaymentInfo;
    onPaymentCompleted: (
        paymentInfo: PaymentInfo | GiftcardPaymentInfo,
        paymentGatewayResponse: any,
    ) => void;
}

function getCheckoutLanguage(preferredLocale: string) {
    switch (preferredLocale) {
        case 'dk':
        case 'da-DK':
            return 'da-DK';

        case 'se':
        case 'sv-SE':
            return 'sv-SE';

        case 'no':
        case 'nb':
        case 'nn':
        case 'nn-NO':
        case 'nb-NO':
            return 'nb-NO';

        case 'de':
        case 'de-DE':
            return 'de-DE';

        case 'pl':
        case 'pl-PL':
            return 'pl-PL';

        case 'fr':
        case 'fr-FR':
            return 'fr-FR';

        case 'es':
        case 'es-ES':
            return 'es-ES';

        case 'nl':
        case 'nl-NL':
            return 'nl-NL';

        case 'fi':
        case 'fi-FI':
            return 'fi-FI';

        default:
            return 'en-GB';
    }
}

export default function NetsPayment(props: IProps): JSX.Element {
    const { locale } = useLocale();
    const mountPoint = useContext(MountPointContext);
    // Unique per instance: two payments can be mounted at once (e.g. an abandoned member-card
    // payment plus the checkout), and duplicate ids would make Nets inject into the wrong one.
    const [containerId] = useState(() => `dibs-complete-checkout-${++netsContainerCount}`);
    useEffect(() => {
        // Registered here rather than in the render body so lookup priority follows checkout
        // initialization order, not whichever mounted instance happened to re-render last.
        setupDocumentToSearchShadowRoot(mountPoint.shadowRoot);
        const isGiftcard = isGiftcardPaymentInfo(props.context);
        const paymentType = isGiftcard ? 'giftcard' : 'booking';
        const { checkoutKey, paymentId } = getPaymentInfoByType(props.context, paymentType);

        const checkoutOptions = {
            checkoutKey,
            paymentId,
            containerId,
            language: getCheckoutLanguage(locale),
        };
        const checkout = new window.Dibs.Checkout(checkoutOptions);

        checkout.on('payment-completed', function (paymentGatewayResponse: any) {
            props.onPaymentCompleted(props.context, paymentGatewayResponse);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps -- Intentionally run only once to avoid re-initializing checkout
    }, []);

    return <div id={containerId} />;
}

function getPaymentInfoByType(
    context: PaymentInfo | GiftcardPaymentInfo,
    type: 'giftcard' | 'booking',
) {
    switch (type) {
        case 'giftcard': {
            return {
                checkoutKey: (context as GiftcardPaymentInfo).giftcard.checkout_key,
                paymentId: (context as GiftcardPaymentInfo).giftcard.payment_id,
            };
        }
        default: {
            return {
                checkoutKey: (context as PaymentInfo).checkout_key,
                paymentId: (context as PaymentInfo).payment_id,
            };
        }
    }
}

// Nets resolves its container (and elements it creates itself) via document.getElementById, which
// cannot see into shadow DOM. Patch it once per page load to fall back to searching every shadow
// root that has hosted a Nets payment, newest first. Several widgets (the checkout, the member-card
// purchase widget) render payments in different shadow roots within one page session, so capturing
// a single root would leave the patch pointing at a stale root for every later payment. The patch
// stays installed for the page lifetime: restoring the original on unmount would break a
// still-mounted sibling payment.
declare const document: any;
const netsShadowRoots: ShadowRoot[] = [];
// Kept module-local (not stored on `document`) so another bundle shipping its own copy of this
// patch chains through ours instead of recursing into it.
let originalGetElementById: ((id: string) => Element | null) | null = null;
function setupDocumentToSearchShadowRoot(shadowRoot: ShadowRoot) {
    // Move (or insert) this root to the end so the most recently initialized payment wins lookups.
    const existingIndex = netsShadowRoots.indexOf(shadowRoot);
    if (existingIndex !== -1) {
        netsShadowRoots.splice(existingIndex, 1);
    }
    netsShadowRoots.push(shadowRoot);

    if (originalGetElementById) {
        return;
    }
    originalGetElementById = document.getElementById.bind(document);
    document.getElementById = (id: string) => {
        const res = originalGetElementById!(id);
        if (res) {
            return res;
        }
        // Native getElementById('') returns null, while querySelector('#') would throw.
        if (!id) {
            return null;
        }
        for (let i = netsShadowRoots.length - 1; i >= 0; i--) {
            const match = netsShadowRoots[i].querySelector('#' + CSS.escape(id));
            // Skip matches inside widgets whose host element has been removed from the page.
            if (match?.isConnected) {
                return match;
            }
        }
        return null;
    };
}
